import React, { useEffect } from 'react'
import {
  Table, Button, Card, Form, Input, Select, message, Drawer, Space, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ThunderboltOutlined, CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type InjectingVoltage } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'
import './MasterForm.css'

const BASE = '/master/injecting-voltages'

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const InjectingVoltageMaster: React.FC = () => {
  const { injectingVoltages, deleteInjectingVoltage, saveInjectingVoltage, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = React.useState<InjectingVoltage | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = React.useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState('')

  const activeCount = injectingVoltages.filter(v => v.status === 'active').length

  const filteredVoltages = injectingVoltages.filter(v => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      v.value.toLowerCase().includes(q) ||
      (v.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || v.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
    } else if (mode === 'edit') {
      const rec = injectingVoltages.find(v => v.id === editId)
      if (rec) form.setFieldsValue(rec)
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, injectingVoltages])

  const handleDelete = async (id: string) => {
    try {
      await deleteInjectingVoltage(id)
      message.success('Injecting Voltage removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let id = isEdit ? editId! : ''
    if (!isEdit) {
      try { id = await generateCode('injecting_voltages') } catch { id = genId('IV') }
    }
    const payload = { ...values, id }
    setSubmitting(true)
    try {
      await saveInjectingVoltage(payload, isEdit)
      message.success(isEdit ? 'Injecting Voltage updated' : 'Injecting Voltage added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Voltage', dataIndex: 'value', width: 120,
      render: (v: string, r: InjectingVoltage) => (
        <span style={{ ...MONO, fontWeight: 700, fontSize: 15, color: '#1677ff' }}>
          {v} <span style={{ fontSize: 12, color: '#8A93A6' }}>{r.unit}</span>
        </span>
      ),
    },
    {
      title: 'Description', dataIndex: 'description',
      render: (v: string) => <span style={{ color: '#4A5568', fontSize: 13 }}>{v || '—'}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: InjectingVoltage) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Injecting Voltage' : 'Add Injecting Voltage'}
        subtitle="Injecting Voltage used for Drawl Voltage (consumer) and Injecting Voltage (generator)"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 640 }}>
          <Form.Item name="value" label="Voltage Value" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. 33" style={MONO} addonAfter="kV" />
          </Form.Item>
          <Form.Item name="unit" label="Unit" initialValue="kV">
            <Input style={MONO} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="e.g. 33 kV sub-transmission voltage" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select options={[
              { value: 'active',   label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update' : 'Add Injecting Voltage'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={4} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThunderboltOutlined style={{ color: '#F59E0B' }} /> Injecting Voltage Master
          </h2>
          <p className="page-subtitle">Injecting Voltages used for Drawl Voltage (consumer) and Injecting Voltage (generator)</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Injecting Voltage</Button>
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Levels', value: injectingVoltages.length, icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by voltage or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredVoltages}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} records` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, ...MONO }}>{viewRecord?.value} {viewRecord?.unit}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={360}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
                {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            <DetailRow label="Voltage"     value={<span style={MONO}>{viewRecord.value} {viewRecord.unit}</span>} />
            <DetailRow label="Description" value={viewRecord.description} />
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default InjectingVoltageMaster
